import { FC } from "react";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { CRMAccountCard } from "../CRMAccountCard";
import { CRMAccountTile } from "../CRMAccountTile";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { ReactComponent as WebChatIcon } from "./assets/web-chat.svg";
import { ReactComponent as WebChatBorderedIcon } from "./assets/web-chat-2.svg";
import { useWebChatBotAccount } from "../../../../providers/WebChatBotAccountProvider/useWebChatBotAccount";
import defaultAvatar from "./assets/default-avatar.png";
import { useManageAccountsDrawer } from "../../providers/ManageAccountsDrawerContext";

interface WebChatBotAccountCardProps {
  variant?: "card" | "tile";
  isClickable?: boolean;
}

export const WebChatBotAccountCard: FC<WebChatBotAccountCardProps> = ({
  variant,
  isClickable,
}) => {
  const { t } = useTranslations();
  const { entity } = useEntitySelectedProvider();
  const { openManageAccountDrawer } = useManageAccountsDrawer();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();
  const { webChatBotAccount, isConnected, connectWebChatBotAccount } =
    useWebChatBotAccount();

  if (variant === "tile") {
    const handleClick = () => {
      if (!isClickable) {
        return;
      }
      if (webChatBotAccount) {
        transitionTo({ name: "webChatBotAccount" });
      } else {
        connectWebChatBotAccount().then(() =>
          openManageAccountDrawer(undefined)
        );
      }
    };

    return (
      <CRMAccountTile
        title={
          webChatBotAccount?.name ||
          t({
            id: "client-support-page.web-chat",
            defaultMessage: "Web Chat",
          })
        }
        isConnected={!!webChatBotAccount}
        onClick={handleClick}
        img={entity.logoImg || undefined}
        defaultAvatar={defaultAvatar}
        icon={WebChatBorderedIcon}
        description={
          webChatBotAccount
            ? isConnected
              ? t({
                  id: "client-support-page.web-live-chat-is-enabled-tile",
                  defaultMessage: "Web Chat is live",
                })
              : t({
                  id: "client-support-page.web-live-chat-is-disabled-tile",
                  defaultMessage: "Web Chat is disabled",
                })
            : t({
                id: "client-support-page.web-live-chat-is-not-connected-tile",
                defaultMessage:
                  "Connect with visitors instantly. Power up your business communications with Web Chat 🤩",
              })
        }
      />
    );
  }

  if (!webChatBotAccount) return null;

  return (
    <CRMAccountCard
      title={
        webChatBotAccount.name ||
        t({
          id: "client-support-page.web-chat",
          defaultMessage: "Web Chat",
        })
      }
      onClick={
        isClickable
          ? () => transitionTo({ name: "webChatBotAccount" })
          : undefined
      }
      // We consider the web chat bot always enabled for now
      switchProps={
        // !hideReconnectOption
        //   ? {
        //       isChecked: isConnected,
        //       onChange: toggleWebChatBotAccount,
        //       isDisabled: isToggleWebChatBotAccountLoading,
        //     }
        //   : undefined
        undefined
      }
      icon={WebChatIcon}
      description={t({
        id: "client-support-page.enhance-support-with-web-chat",
        defaultMessage:
          "Enhance customer support with integrated Web Chat on your website",
      })}
    />
  );
};
